import { EFinancingProgram } from '@if/core/types'
import { ControlledCheckbox, ControlledSelect, useFormDialog } from '@if/mui'
import { ListSubheader, MenuItem, Stack } from '@mui/material'
import { MerchantAutocomplete } from '@src/components'
import { useCopyCreditApplicationAsDraft } from '@src/data/api/credit-application/credit-application-api'
import { useMerchantsForProgram } from '@src/data/api/merchant/merchant-api'
import {
  useFeatureFlags,
  useFinancingConfig,
  useProgramsSupportingCopyAsDraft,
} from '@src/data/selectors/app-config-selectors'
import { CreditApplication } from '@src/data/types'
import { CopyAsDraftForm, copyAsDraftOptionsSchema, copyAsDraftSchema } from '@src/data/types/CopyAsDraftSchema'
import { getProgramsByCompanyId } from '@src/data/types/FinancingCompanies'
import { useUser } from '@src/providers/UserProvider'
import { useCallback, useEffect, useMemo } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

export interface CopyLoanAsDraftSource {
  creditApplicationId: string
  financingProgramId: EFinancingProgram
  merchantId: string
}

interface CopyLoanAsDraftFieldsProps {
  form: UseFormReturn<CopyAsDraftForm>
  source: CopyLoanAsDraftSource
}

const CopyLoanAsDraftFields = ({ form, source }: CopyLoanAsDraftFieldsProps) => {
  const { t } = useTranslation()
  const user = useUser()
  const featureFlags = useFeatureFlags()
  const programsSupportingCopy = useProgramsSupportingCopyAsDraft()
  const { control, setValue, watch } = form

  const selectedProgram = watch('financingProgramId')
  const selectedMerchantId = watch('merchantId')

  const programOptions = useMemo(() => {
    const programsByCompany = user.companyIds
      .map((companyId) => ({
        companyId,
        programs: getProgramsByCompanyId([companyId], featureFlags).filter((x) => programsSupportingCopy.includes(x)),
      }))
      .filter((x) => x.programs.length > 0)

    return programsByCompany.flatMap(({ companyId, programs }) => [
      ...(programsByCompany.length > 1 ? [<ListSubheader key={companyId}>{companyId}</ListSubheader>] : []),
      ...programs.map((program) => (
        <MenuItem key={program} value={program}>
          {t(`EFinancingProgram.${program}`, { ns: 'enum' })}
        </MenuItem>
      )),
    ])
  }, [user.companyIds, featureFlags, programsSupportingCopy, t])

  const hasSingleMerchant = useFinancingConfig(selectedProgram).hasSingleMerchant === true
  const [programMerchants] = useMerchantsForProgram(hasSingleMerchant ? selectedProgram : null)
  const lockedMerchant = hasSingleMerchant && programMerchants.length === 1 ? programMerchants[0] : null

  useEffect(() => {
    if (lockedMerchant && selectedMerchantId !== lockedMerchant.id) {
      setValue('merchantId', lockedMerchant.id, { shouldValidate: true })
    }
  }, [lockedMerchant, selectedMerchantId, setValue])

  // Merchants belong to a program, so they don't survive a program change
  const handleProgramChanged = useCallback(
    (program: EFinancingProgram | null) => {
      setValue('merchantId', program === source.financingProgramId ? source.merchantId : '')
    },
    [source, setValue],
  )

  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      {featureFlags.allowProgramAndMerchantChangeOnDraft && (
        <>
          <ControlledSelect
            control={control}
            name="financingProgramId"
            label={t('editCreditApplication.copyAsDraft.financingProgram')}
            options={programOptions}
            onChange={handleProgramChanged}
            disableEmptyOption
            fullWidth
          />
          <MerchantAutocomplete
            key={selectedProgram}
            control={control}
            name="merchantId"
            label={t('common.merchant')}
            financingProgramId={selectedProgram}
            disabled={!!lockedMerchant}
          />
        </>
      )}
      <Stack>
        <ControlledCheckbox
          control={control}
          name="copyMessages"
          label={t('editCreditApplication.copyAsDraft.copyMessages')}
        />
        <ControlledCheckbox
          control={control}
          name="copyDocuments"
          label={t('editCreditApplication.copyAsDraft.copyDocuments')}
        />
        <ControlledCheckbox
          control={control}
          name="copyCoapplicant"
          label={t('editCreditApplication.copyAsDraft.copyCoapplicant')}
        />
      </Stack>
    </Stack>
  )
}

export const useCopyLoanAsDraftDialog = () => {
  const { t } = useTranslation()
  const [copyCreditApplicationAsDraft] = useCopyCreditApplicationAsDraft()
  const { allowProgramAndMerchantChangeOnDraft } = useFeatureFlags()

  return useFormDialog<CopyAsDraftForm, CopyLoanAsDraftSource, CreditApplication>({
    title: t('editCreditApplication.copyAsDraft.title'),
    confirmLabel: t('common.copy'),
    maxWidth: 'sm',
    fullWidth: true,
    schema: allowProgramAndMerchantChangeOnDraft ? copyAsDraftSchema : copyAsDraftOptionsSchema,
    defaultValues: (source) => ({
      financingProgramId: source.financingProgramId,
      merchantId: source.merchantId,
    }),
    content: ({ form, props }) => <CopyLoanAsDraftFields form={form} source={props} />,
    // Always sent while the FS is on: the backend compares them with the original application,
    // so an unchanged program and merchant is a plain copy (see CopyCreditApplicationService.CopyAsDraft)
    onSubmit: ({ financingProgramId, merchantId, ...options }, { props: source }) =>
      copyCreditApplicationAsDraft({
        creditApplicationId: source.creditApplicationId,
        financingProgramId: source.financingProgramId,
        ...options,
        ...(allowProgramAndMerchantChangeOnDraft
          ? { newFinancingProgramId: financingProgramId, newMerchantId: merchantId }
          : {}),
      }),
  })
}
